import { Box, Grid, SxProps, useTheme, useMediaQuery } from '@mui/material';
import React, { Dispatch, ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CAppInfoTooltip from '../../../../app/components/CAppInfoTooltip';
import CAppPaper from '../../../../app/components/CAppPaper';
import AppFormatAmount from '../../../../app/features/format/AppFormatAmount';
import AppFormatPercent from '../../../../app/features/format/AppFormatPercent';
import AppFormatX from '../../../../app/features/format/AppFormatX';
import CDateUpdatedAtom from '../../components/CDateUpdatedAtom';
import investmentKpiLang from '../../langs/investmentKpiLang';
import langInvestWordLang from '../../langs/langInvestWordLang';
import IFund from '../../interfaces/fund/IFund';
import useInvestFundPerformances from '../../hooks/fund/useInvestFundPerformances';
import IFundFilters from '../../interfaces/fund/IFundFilters';
import langInvestFundPerformancesPrivateCreditLang from '../../langs/langInvestFundPerformancesPrivateCreditLang';

type VInvestFundPerformancePrivateCreditProps = {
	fund: IFund;
	filters: IFundFilters;
	setDataUpdatedOn: Dispatch<Date>;
};

const VInvestFundPerformancePrivateCredit = (props: VInvestFundPerformancePrivateCreditProps) => {
	const { fund, filters, setDataUpdatedOn } = props;

	const { capital, performance } = useInvestFundPerformances(fund?.id, filters);
	const { t } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	useEffect(() => {
		capital && setDataUpdatedOn(capital.dataUpdatedOn);
	}, [capital, setDataUpdatedOn]);

	const styleTitle = {
		color: theme.palette.secondary.main,
		boxSizing: 'inherit',
		textAlign: 'left',
		fontWeight: '500',
		fontSize: '1.25rem',
		lineHeight: '2rem',
		'& .icon': {
			marginRight: '10px',
		},
		'& small': {
			fontSize: '0.9rem',
		},
	};
	const styleKPI = {
		padding: '30px',
		border: '0px',
		borderRadius: '10px',
		textAlign: 'left',
		'& .title': styleTitle,
		'& .title-position': {
			color: '#FFF',
			lineHeight: isMobile ? '1.5' : '2rem',
			fontWeight: '600',
			fontSize: '0.9rem',
			textAlign: 'right',
		},
		'& .title-total': {
			color: '#FFF',
			fontWeight: '400',
			fontSize: '0.9rem',
			textAlign: 'right',
			lineHeight: isMobile ? '1.5' : '2rem',
		},
		'& .list': {
			color: 'white',
			textAlign: 'left',
			alignItems: 'top',
			padding: '10px 0px 0px 0px',
			fontWeight: 'normal',
			'& .item': {
				color: 'white',
				textAlign: 'left',
				alignItems: 'center',
				padding: '6px 0px',
				minHeight: '3rem',
				fontWeight: 'normal',
				'& .value': {
					textAlign: 'right',
					fontWeight: '600',
					fontSize: isMobile ? '0.8rem' : '1.25rem',
				},
				'& .total': {
					textAlign: 'right',
					fontWeight: '400',
					fontSize: isMobile ? '0.8rem' : '1.25rem',
					//lineHeight: '2.4rem',
				},
				'& .label': {
					textAlign: 'left',
					fontSize: isMobile ? '0.9rem' : '1rem',
				},
			},
			'& .item:last-child': {
				paddingBottom: '0px',
			},
		},
	} as SxProps;

	return (
		<>
			<Grid container spacing={3}>
				<Grid item sm={7} xs={12}>
					<CAppPaper
						id="fund-performance-capital-block"
						variant="outlined"
						displayBackgroundImage
						color="primary"
						sx={styleKPI}
					>
						<Grid container spacing={3}>
							<Grid item sm={6} xs={12}>
								<Box className="title">
									{t(langInvestFundPerformancesPrivateCreditLang.title_capital.tag)} &nbsp;
									<small>
										<CDateUpdatedAtom
											isEstimated={capital?.isEstimated}
											date={capital ? capital.dataUpdatedOn : null}
											dateOnly
										/>{' '}
										|{' '}
										<i>
											{capital?.isEstimated
												? t(langInvestWordLang.estimated.tag)
												: t(langInvestWordLang.audited.tag)}
										</i>
									</small>
								</Box>
							</Grid>
							<Grid item xs={6} sx={{ display: { sm: 'none' } }} />
							<Grid item xs={3} sm={3}>
								<Box className="title-position">
									{t(langInvestFundPerformancesPrivateCreditLang.text_your_position.tag)}
								</Box>
							</Grid>
							<Grid item xs={3}>
								<Box className="title-total">
									{t(langInvestFundPerformancesPrivateCreditLang.text_fund_total.tag)}
								</Box>
							</Grid>
						</Grid>
						<Box className="list">
							{!fund.isOpenEndedFund && (
								<CapitalKpiLine
									label={t(langInvestFundPerformancesPrivateCreditLang.committed_capital.tag)}
									value={
										<AppFormatAmount
											value={capital?.kpis.committed?.value}
											addSpace
											textIfNull={t(langInvestWordLang.nc.tag)}
											currency={fund.currency}
										/>
									}
									total={
										<AppFormatAmount
											value={capital?.kpis.committed?.total}
											addSpace
											textIfNull={t(langInvestWordLang.nc.tag)}
											currency={fund.currency}
										/>
									}
								/>
							)}
							<CapitalKpiLine
								label={t(langInvestFundPerformancesPrivateCreditLang.called_capital.tag)}
								value={
									<AppFormatAmount
										value={capital?.kpis.called?.value}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={fund.currency}
									/>
								}
								total={
									<AppFormatAmount
										value={capital?.kpis.called?.total}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={fund.currency}
									/>
								}
							/>
							{!fund.isOpenEndedFund && (
								<CapitalKpiLine
									label={
										<>
											{t(langInvestFundPerformancesPrivateCreditLang.callable_capital.tag)}
											<CAppInfoTooltip
												title={t(investmentKpiLang.callable_capital_tooltip.tag)}
											/>
										</>
									}
									value={
										<AppFormatAmount
											value={capital?.kpis.callable?.value}
											addSpace
											textIfNull={t(langInvestWordLang.nc.tag)}
											currency={fund.currency}
										/>
									}
									total={
										<AppFormatAmount
											value={capital?.kpis.callable?.total}
											addSpace
											textIfNull={t(langInvestWordLang.nc.tag)}
											currency={fund.currency}
										/>
									}
								/>
							)}
							<CapitalKpiLine
								label={t(langInvestFundPerformancesPrivateCreditLang.total_distributed.tag)}
								labelSub={t(langInvestFundPerformancesPrivateCreditLang.interest_income.tag)}
								value={
									<AppFormatAmount
										value={capital?.kpis.distributedInterestIncome?.value}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={fund.currency}
									/>
								}
								total={
									<AppFormatAmount
										value={capital?.kpis.distributedInterestIncome?.total}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={fund.currency}
									/>
								}
							/>
							<CapitalKpiLine
								label={t(langInvestFundPerformancesPrivateCreditLang.total_distributed.tag)}
								labelSub={t(langInvestFundPerformancesPrivateCreditLang.return_of_capital.tag)}
								value={
									<AppFormatAmount
										value={capital?.kpis.distributedReturnOfCapital?.value}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={fund.currency}
									/>
								}
								total={
									<AppFormatAmount
										value={capital?.kpis.distributedReturnOfCapital?.total}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={fund.currency}
									/>
								}
							/>
							<CapitalKpiLine
								label={t(langInvestFundPerformancesPrivateCreditLang.net_called_capital.tag)}
								value={
									<AppFormatAmount
										value={capital?.kpis.netCalled?.value}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={fund.currency}
									/>
								}
								total={
									<AppFormatAmount
										value={capital?.kpis.netCalled?.total}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={fund.currency}
									/>
								}
							/>
							<CapitalKpiLine
								label={
									<>
										{t(langInvestFundPerformancesPrivateCreditLang.asset_outstanding_capital.tag)}
										<CAppInfoTooltip
											title={t(
												langInvestFundPerformancesPrivateCreditLang
													.asset_outstanding_capital_tooltip.tag,
											)}
										/>
									</>
								}
								labelSub={t(
									langInvestFundPerformancesPrivateCreditLang.asset_outstanding_capital_sub_label.tag,
								)}
								value={
									<AppFormatAmount
										value={capital?.kpis.assetOutstandingCapital?.value}
										addSpace
										textIfNull={t(langInvestWordLang.na.tag)}
										currency={fund.currency}
									/>
								}
								total={
									<AppFormatAmount
										value={capital?.kpis.assetOutstandingCapital?.total}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={fund.currency}
									/>
								}
							/>
						</Box>
					</CAppPaper>
				</Grid>
				<Grid item sm={5} xs={12}>
					<CAppPaper
						id="fund-performance-kpis-block"
						variant="outlined"
						displayBackgroundImage
						color="primary"
						sx={styleKPI}
					>
						<Grid container spacing={3}>
							<Grid item sm={8} xs={12}>
								<Box className="title">
									{t(langInvestFundPerformancesPrivateCreditLang.title_performance.tag)} &nbsp;
									<small>
										{performance && performance.reportingPeriod
											? performance.reportingPeriod.name
											: ''}{' '}
										|{' '}
										<i>
											{performance && performance.isLiquidationQuarter
												? t(langInvestWordLang.liquidated.tag)
												: performance && performance.isEstimated
												? t(langInvestWordLang.estimated.tag)
												: t(langInvestWordLang.audited.tag)}
										</i>
									</small>
								</Box>
							</Grid>
							<Grid item xs={7} sx={{ display: { sm: 'none' } }} />
							<Grid item xs={5} sm={4}>
								<Box className="title-position">
									{t(langInvestFundPerformancesPrivateCreditLang.text_your_position.tag)}
								</Box>
							</Grid>
						</Grid>

						<Box className="list">
							<PerformanceKpiLine
								label={t(langInvestFundPerformancesPrivateCreditLang.nav.tag)}
								value={
									<AppFormatAmount
										value={performance?.kpisPosition.netAssetValue ?? undefined}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={performance?.kpisPosition?.currency}
									/>
								}
							/>
							<PerformanceKpiLine
								label={t(langInvestFundPerformancesPrivateCreditLang.irr.tag)}
								value={
									<AppFormatPercent
										value={performance?.kpisPosition.irr}
										isNS={performance?.kpisPosition.irrIsNs ?? undefined}
										textIfNull={t(langInvestWordLang.nc.tag)}
									/>
								}
							/>
							<PerformanceKpiLine
								label={
									<>
										{t(langInvestFundPerformancesPrivateCreditLang.arr.tag)}
										<CAppInfoTooltip title={t(investmentKpiLang.arr_tooltip.tag)} />
									</>
								}
								value={
									<AppFormatPercent
										value={performance?.kpisPosition.arr}
										isNS={performance?.kpisPosition.arrIsNs ?? undefined}
										textIfNull={t(langInvestWordLang.nc.tag)}
									/>
								}
							/>
							<PerformanceKpiLine
								label={
									<>
										{t(langInvestFundPerformancesPrivateCreditLang.total_value.tag)}
										<CAppInfoTooltip title={t(investmentKpiLang.total_value_tooltip.tag)} />
									</>
								}
								value={
									<AppFormatAmount
										value={performance?.kpisPosition.totalValue ?? undefined}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={performance?.kpisPosition?.currency}
									/>
								}
							/>
							<PerformanceKpiLine
								label={
									<>
										{t(langInvestFundPerformancesPrivateCreditLang.tvpi.tag)}
										<CAppInfoTooltip title={t(investmentKpiLang.tvpi_tooltip.tag)} />
									</>
								}
								value={
									<AppFormatX
										value={performance ? performance.kpisPosition.tvpi : undefined}
										textIfNull={t(langInvestWordLang.nc.tag)}
									/>
								}
							/>
							<PerformanceKpiLine
								label={t(langInvestFundPerformancesPrivateCreditLang.dpi.tag)}
								value={
									<AppFormatX
										value={performance ? performance.kpisPosition.dpi : undefined}
										textIfNull={t(langInvestWordLang.nc.tag)}
									/>
								}
							/>
							<PerformanceKpiLine
								label={t(langInvestFundPerformancesPrivateCreditLang.rvpi.tag)}
								value={
									<AppFormatX
										value={performance ? performance.kpisPosition.rvpi : undefined}
										textIfNull={t(langInvestWordLang.nc.tag)}
									/>
								}
							/>
						</Box>
					</CAppPaper>
				</Grid>
			</Grid>
		</>
	);
};

type CapitalKpiLineProps = {
	label: string | ReactNode;
	labelSub?: string | ReactNode;
	value: string | ReactNode;
	total: string | ReactNode;
};
const CapitalKpiLine = (props: CapitalKpiLineProps) => {
	const { label, labelSub, value, total } = props;

	return (
		<Box className="item">
			<Grid container spacing={3} alignItems="top" justifyContent="top">
				<Grid item xs={6} className="label">
					<Box>
						<Box>{label}</Box>
						{labelSub ? <Box sx={{ lineHeight: '1.2rem', fontSize: '0.8rem' }}>{labelSub}</Box> : ''}
					</Box>
				</Grid>
				<Grid item xs={3} className="value">
					{value}
				</Grid>
				<Grid item xs={3} className="total">
					{total}
				</Grid>
			</Grid>
		</Box>
	);
};

type PerformanceKpiLineProps = {
	label: string | ReactNode;
	value: string | ReactNode;
};
const PerformanceKpiLine = (props: PerformanceKpiLineProps) => {
	const { label, value } = props;

	return (
		<Box className="item">
			<Grid container spacing={3} alignItems="top" justifyContent="top">
				<Grid item xs={8} className="label">
					<Box>{label}</Box>
				</Grid>
				<Grid item xs={4} className="value">
					{value}
				</Grid>
			</Grid>
		</Box>
	);
};

export default VInvestFundPerformancePrivateCredit;
